import { useCallback, useRef } from 'react';
import { useFocusEffect, useLocalSearchParams, usePathname } from 'expo-router';

import { useNavigationReset } from '../../../../routing';

// ─────────────────────────────────────────────────────────────────────────────

// Here we handle 301 redirects using the redirects mapping.
export const usePermanentRedirects = () => {
  const pathname = usePathname();
  const searchParams = useLocalSearchParams();
  const resetNavigation = useNavigationReset();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  // Search params are a new object on every render, so the focus effect reads
  // them from a ref instead of re-running (and resetting) on each render.
  const searchParamsRef = useRef(searchParams);

  searchParamsRef.current = searchParams;

  // ─── Derived ───────────────────────────────────────────────────────────────

  const redirectTo = pathname ? REDIRECTS[pathname] : null;

  // ─── Effects ───────────────────────────────────────────────────────────────

  useFocusEffect(
    useCallback(() => {
      if (redirectTo) {
        resetNavigation(
          getResetState({ mapping: redirectTo, searchParams: searchParamsRef.current }),
        );
      }
    }, [redirectTo, resetNavigation]),
  );

  return Boolean(redirectTo);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getResetState(params: Params): ResetState {
  const { mapping, searchParams } = params;

  const { tabName, screenName } = mapping;

  // The `not-found` catch-all segments describe the legacy path itself, so
  // only the link parameters are carried over to the new screen.
  const { 'not-found': _notFoundSegments, ...linkParams } = searchParams;

  const screenRoute = { name: screenName, params: linkParams };
  const screenRoutes = tabName === 'account' ? [{ name: 'index' }, screenRoute] : [screenRoute];

  return {
    index: 0,
    routes: [
      {
        name: '(tabs)',
        state: { routes: [{ name: tabName, state: { routes: screenRoutes } }] },
      },
    ],
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const REDIRECTS: Record<string, TabRouteMapping> = {
  '/location': { tabName: '(menu)', screenName: 'locations/index' },
  '/menu': { tabName: 'account', screenName: 'menu/index' },
  '/profile': { tabName: 'account', screenName: 'info/index' },
  '/account/profile': { tabName: 'account', screenName: 'info/index' },
  '/challenges': { tabName: 'rewards', screenName: 'index' },
  '/sg-rewards': { tabName: 'rewards', screenName: 'index' },
  '/payment': { tabName: 'account', screenName: 'payments/index' },
  '/account/payment-and-gift-cards': { tabName: 'account', screenName: 'payments/index' },
  '/addresses': { tabName: 'account', screenName: 'addresses/index' },
  '/orders': { tabName: 'account', screenName: 'orders/index' },
  '/favorites': { tabName: 'account', screenName: 'favorites/index' },
  '/account-dietary-restrictions': { tabName: 'account', screenName: 'dietary-restrictions/index' },
};

// ─── Types ───────────────────────────────────────────────────────────────────

type TabRouteMapping = {
  tabName: string;
  screenName: string;
};

type Params = {
  mapping: TabRouteMapping;
  searchParams: ReturnType<typeof useLocalSearchParams>;
};

type ResetState = Parameters<ReturnType<typeof useNavigationReset>>[0];
